import { useState } from "react";
import instructionsConfig from "./instructions.json";
import type { Browser } from "../detectBrowser";

interface InstructionsViewProps {
    browser: Browser;
}

interface BrowserInstructions {
    name: string;
    firstPasteGesture?: string;
    steps: string[];
}

interface InstructionsConfig {
    jwtSecurityWarning: string;
    selfXssWarning: string;
    snippet: string;
    common: { preface: string };
    browsers: Record<Browser, BrowserInstructions>;
}

export function InstructionsView({ browser }: InstructionsViewProps) {
    const config = instructionsConfig as InstructionsConfig;
    const entry = config.browsers[browser];
    const [copied, setCopied] = useState(false);

    // No transient reset here — the no-silent-refresh static check
    // (tools/no-silent-refresh.sh) bans `setTimeout` anywhere in
    // src/web/src/auth/ to prevent background-renewal patterns from
    // sneaking in. "Copied!" persists; visually equivalent to a
    // permanent confirmation. If the user clicks again, the label
    // stays "Copied!" — harmless.
    async function copySnippet() {
        await navigator.clipboard.writeText(config.snippet);
        setCopied(true);
    }

    return (
        <div data-testid={`instructions-${browser}`}>
            <div className="callout callout-warn" data-testid="jwt-warning">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                <span>{config.jwtSecurityWarning}</span>
            </div>
            <p style={{ color: "var(--n8)", marginBottom: 8 }}>{config.common.preface}</p>
            <p style={{ color: "var(--n9)", marginBottom: 4 }}>
                <strong>For {entry.name}:</strong>
            </p>
            {entry.firstPasteGesture && (
                <div className="callout callout-info" data-testid="paste-gesture-callout">
                    <i className="fa-solid fa-circle-info" aria-hidden="true" />
                    <span>{entry.firstPasteGesture}</span>
                </div>
            )}
            <ol style={{ color: "var(--n8)" }}>
                {entry.steps.map((step, i) => (
                    <li key={i}>{step}</li>
                ))}
            </ol>
            <div className="callout callout-warn" data-testid="self-xss-warning">
                <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
                <span>{config.selfXssWarning}</span>
            </div>
            <div data-testid="console-snippet-wrapper" style={{ position: "relative", marginTop: 8 }}>
                <pre
                    data-testid="console-snippet"
                    style={{
                        background: "var(--n1)",
                        border: "1px solid var(--n3)",
                        borderRadius: "var(--r-sm)",
                        padding: "10px 12px",
                        paddingRight: 96,
                        fontSize: 12,
                        fontFamily: "ui-monospace, 'SF Mono', monospace",
                        overflowX: "auto",
                        margin: 0,
                        color: "var(--n9)",
                    }}
                >
                    <code>{config.snippet}</code>
                </pre>
                <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    data-testid="console-snippet-copy"
                    onClick={copySnippet}
                    style={{ position: "absolute", top: 6, right: 6 }}
                    aria-label="Copy snippet to clipboard"
                >
                    <i className="fa-regular fa-copy" aria-hidden="true" /> {copied ? "Copied!" : "Copy"}
                </button>
            </div>
        </div>
    );
}
